/**
 * The Tasks tab — Step 46 / 117.
 *
 * **A task is a TICKET here, not a sub-record.** That is a deliberate
 * departure from the reference, and it is Kai's:
 *
 *   *"different to freshservice, I want tasks to appear as tickets, with a
 *   group and assignee, but pretty much nothing else"* · *"someone should be
 *   able to create a task ticket to do something unrelated to a ticket"* ·
 *   *"say someone is a new starter, a task that goes out to security for the
 *   pass, and helpdesk for the id creation, legal for sign off, reception for
 *   first day starter."*
 *
 * That last one is the case that settles the model. Those four bits of work
 * belong to four different teams, and as ticket-type rows each one lands in
 * THAT team's own queue with its own assignee, its own audit trail and its
 * own reference. A child table would have hidden Security's job inside
 * somebody else's ticket, where Security never looks.
 *
 * It also costs nothing: numbers, search, the queue, permissions and the
 * activity feed all already work on tickets, which is exactly what the plan's
 * "one table with a type discriminator" was for.
 *
 * So this tab is a VIEW over child tickets of type `task` — the list is the
 * same `/children` read the Related tickets tab uses, filtered.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { apiFetch } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { ticketRef } from '@itsm/shared';
import { ticketsApi } from './api';
import { ticketPath } from './api';
import { cn } from '../../lib/utils';
import { escapeHtml } from '../../lib/escapeHtml';
import { useDirty } from '../../lib/dirty';

interface ChildRow {
  id: string;
  number: number;
  type: string;
  subject: string;
  priority: string;
  /** The status KEY and its category, resolved server-side — the child read
   *  used to return the status ID, which nothing here could map to a name. */
  status: string | null;
  statusCategory: string | null;
  groupName: string | null;
}

export function TasksTab({
  id,
  requesterEmail,
  statuses,
  canEdit,
}: {
  id: string;
  /** The parent's requester, so a task raised here belongs to the same person
   *  the work is for rather than to whoever happened to click Add. */
  requesterEmail: string;
  statuses: { key: string; name: string; category: string }[];
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  // URL state on purpose: this is "which group the NEXT task goes to" — a
  // control on a form, not a filter over anything. Putting it in the URL
  // would put a half-finished form in a shared ticket link.
  const [groupId, setGroupId] = useState('');
  // A typed-but-unadded title is work. Without this, switching tab throws it
  // away silently — which is precisely what the dirty-guard audit exists to
  // catch, and it caught it here.
  const clearDirty = useDirty(!!title.trim());

  const children = useQuery({
    queryKey: qk.tickets.children(id),
    queryFn: () => apiFetch<{ entries: ChildRow[] }>(`/api/tickets/${id}/children`),
  });
  const groups = useQuery({ queryKey: qk.orgGroups, queryFn: ticketsApi.groups, staleTime: 5 * 60_000 });

  const tasks = (children.data?.entries ?? []).filter((c) => c.type === 'task');
  const nameOf = (key: string | null) => statuses.find((s) => s.key === key)?.name ?? key ?? '—';

  const add = useMutation({
    mutationFn: () =>
      ticketsApi.create({
        subject: title.trim(),
        type: 'task',
        priority: 'medium',
        requesterEmail,
        // A task's "description" is its title. The API insists on a body, and
        // inventing a second box for one sentence is exactly the "pretty much
        // nothing else" this is meant to avoid.
        bodyHtml: `<p>${escapeHtml(title.trim())}</p>`,
        parentId: id,
        ...(groupId ? { groupId } : {}),
      }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not add that task.');
      clearDirty();
      setTitle('');
      void qc.invalidateQueries({ queryKey: qk.tickets.children(id) });
      void qc.invalidateQueries({ queryKey: qk.tickets.activity(id) });
    },
  });

  return (
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
      <div>
        <h3 className="text-sm font-semibold">Tasks</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Each task is a ticket of its own, so it lands in its group's queue.
        </p>
      </div>

      {canEdit && (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim()) add.mutate();
          }}
        >
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs doing?"
            className="min-w-48 flex-1"
          />
          <Select value={groupId} onChange={(e) => setGroupId(e.target.value)} className="w-48">
            <option value="">No group</option>
            {(groups.data?.entries ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </Select>
          <Button type="submit" disabled={!title.trim() || add.isPending}>
            <Plus className="h-4 w-4" />
            {add.isPending ? 'Adding…' : 'Add task'}
          </Button>
        </form>
      )}

      {children.isPending && <Skeleton className="h-10 w-full" />}
      {children.data && tasks.length === 0 && (
        <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          No tasks yet.
        </p>
      )}
      <div className="space-y-1.5">
        {tasks.map((t) => {
          // Category, not a name match: a tenant may call it "Fixed".
          const done = t.statusCategory === 'resolved' || t.statusCategory === 'closed';
          return (
            <div
              key={t.id}
              className="flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-accent/40"
            >
              {done ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              {/* The ref AND the subject are the click target. Kai: *"you
                  should be able to just click the subject or number in the
                  tasks tab to open"*. An icon button off to the right is a
                  second, smaller target for the thing you were already
                  pointing at. */}
              <button
                type="button"
                onClick={() => navigate(ticketPath(t.type, t.id))}
                className="flex min-w-0 flex-1 items-center gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {ticketRef(t.type, t.number)}
                </span>
                <span className={cn('min-w-0 flex-1 truncate text-sm', done && 'line-through opacity-60')}>
                  {t.subject}
                </span>
              </button>
              {t.groupName && (
                <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">{t.groupName}</span>
              )}
              <span className="shrink-0 text-xs text-muted-foreground">{nameOf(t.status)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
